import { useMemo, useRef, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, message } from "antd";

import PageHeader from "@/components/UI/PageHeader";
import {
  can,
  hasPlanEditorRole,
  type PlanUser,
} from "@/views/PlanOfRecord/planAccessData";
import { useUser } from "@/contexts/UserContext";
import { getUserRoleForTrial } from "@/components/Insights/insights.utils";
import { useEffectiveAssumedRole } from "@/contexts/AssumedRoleContext";
import { useSelectableTableView, useTableHeight } from "@/utils/tableHelpers";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useFetch, is403ApiError } from "@/hooks/FetchHook";
import useTableState from "@/hooks/useTableState";
import { createDefaultTableState } from "@/utils/workspaceState";
import { ColumnVisibility, ColumnsType } from "@/types/tableState.types";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import {
  constructQueryString,
  prepareUnifiedDataPostRequestBody,
} from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import PaginatedTable from "@/components/UI/PaginatedTable";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import Loader from "@/components/UI/Loader";

import { NewVersionModal } from "@/components/DocumentWorkspace/NewVersionModal";
import { RoleAccessModal } from "@/components/DocumentWorkspace/RoleAccessModal";
import { UploadDocumentModal } from "@/components/DocumentWorkspace/UploadDocumentModal";
import { VersionHistoryDrawer } from "@/components/DocumentWorkspace/VersionHistoryDrawer";
import { createDocumentColumns } from "./documentsTableConfig";
import {
  DOCUMENT_WORKSPACE_DATASET_TYPE,
  buildNewVersionPayload,
  buildUploadPayload,
} from "./documentWorkspaceMapping";
import { useDocumentWorkspaceMutations } from "@/hooks/useDocumentWorkspaceMutations";
import type {
  StudyDocumentData,
  StudyDocumentMetadata,
  StudyDocumentRow,
} from "./documentWorkspace.types";

/**
 * Study Document Workspace — study-scoped uploads with amendment identity and blinding-aware
 * retrieval.
 *
 * Backed by the `study-documents` dataset. Design reasoning in
 * docs/PRD_MOCKUPS_DESIGN_NOTES.md. There is no audit-trail dataset for documents yet, so the
 * mockup's fabricated Audit log tab is gone rather than shown empty-but-implied-complete —
 * see the git history on this file for what it looked like.
 */
export default function DocumentWorkspace() {
  const queryString = constructQueryString({
    baseParams: { dataset_type: DOCUMENT_WORKSPACE_DATASET_TYPE },
  });

  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useFetch({ endpointSuffix: `filters/${queryString}` });

  if (metadataError) {
    return (
      <div className="flex mt-25 justify-center items-center">
        {is403ApiError(metadataError) ? <Error403 /> : <ErrorComponent />}
      </div>
    );
  }

  if (metadataLoading || !metadata) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <DocumentWorkspaceContent metadata={metadata as StudyDocumentMetadata} />
  );
}

function DocumentWorkspaceContent({
  metadata,
}: {
  metadata: StudyDocumentMetadata;
}) {
  const { id } = useScopeParams();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [versioning, setVersioning] = useState<StudyDocumentRow | null>(null);
  const [editingAccess, setEditingAccess] = useState<StudyDocumentRow | null>(
    null,
  );

  // Real identity, not the mockup harness — same fix as Plan of Record's PlanOfRecord.tsx:
  // MockControlsProvider is only mounted under the standalone /mockup-preview.html entry,
  // never on this routed screen, so useMockControls() here always resolved to its null
  // default and every user silently became PLAN_USERS[0] — canManageAccess was permanently
  // true, and Upload/New version had no gate at all regardless.
  const { userInfo } = useUser();
  const grantedRole = getUserRoleForTrial(userInfo, id);
  const actingUser: PlanUser = useMemo(
    () => ({
      id: userInfo?.id ?? "",
      name: userInfo
        ? `${userInfo.first_name} ${userInfo.last_name}`.trim() ||
          userInfo.email
        : "",
      jobTitle: "",
      roleId: hasPlanEditorRole(grantedRole) ? "plan-editor" : "plan-viewer",
      grantedVia: "",
      grantedAt: "",
    }),
    [userInfo, grantedRole],
  );
  // Client-side only — the server-side gate is the plan_editor role check in
  // views_study_documents.py. This only controls which affordances render; a user
  // without the role gets a 403 from the API either way.
  const canEdit = can(actingUser, "plan.edit");
  const canManageAccess = can(actingUser, "documents.manage-access");

  // The mockup's self-selected role switcher (real in the deployed app, but self-asserted
  // via localStorage — see AssumedRoleContext's own docs — not a server-verified claim).
  // useMockControls()'s currentRole was a DIFFERENT, unmounted mock signal that always
  // read null on this routed screen, so this filter never actually filtered anything for
  // a real user; it only ever worked inside the standalone mockup harness.
  const assumedRole = useEffectiveAssumedRole();

  const { uploadDocument, addVersion, setRoleAccess } =
    useDocumentWorkspaceMutations();

  const pageRef = useRef<HTMLDivElement>(null);
  const { tableHeight: maxMenuHeight } = useTableHeight({ tableRef: pageRef });

  const metadataColumns = useMemo(
    () => metadata.columns.map((column) => column.field),
    [metadata.columns],
  );
  const defaultHiddenColumns = useMemo(
    () =>
      metadata.columns
        .filter((column) => Boolean(column.hidden))
        .map((column) => column.field),
    [metadata.columns],
  );
  const metadataFilterKeys = useMemo(
    () => Object.keys(metadata.filters || {}),
    [metadata.filters],
  );

  const initialTableState = useMemo(() => {
    const base = createDefaultTableState(metadataColumns, defaultHiddenColumns);
    return {
      ...base,
      pagination: { ...base.pagination, total: metadata.row_count },
    };
  }, [metadataColumns, defaultHiddenColumns, metadata.row_count]);

  const { currentTableState, tableStateFunctions } = useTableState({
    initialState: initialTableState,
    pageName: DOCUMENT_WORKSPACE_DATASET_TYPE,
    isSingleSubject: false,
    trialId: id,
    metadataColumns,
    metadataFilterKeys,
  });

  const allFiltersData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(metadata.filters || {}).map(([key, filterInfo]) => [
          key,
          {
            data_type: filterInfo.data_type || "text",
            values: filterInfo.values || [],
          },
        ]),
      ),
    [metadata.filters],
  );

  const payload = useMemo(
    () =>
      prepareUnifiedDataPostRequestBody({
        global_filters: currentTableState.filters,
        allFiltersData,
        page: currentTableState.pagination.current,
        pageSize: currentTableState.pagination.pageSize,
        sorting: currentTableState.sorting,
      }),
    [
      currentTableState.filters,
      currentTableState.pagination,
      currentTableState.sorting,
      allFiltersData,
    ],
  );

  const {
    data: documentDataRaw,
    isLoading: loadingDocumentData,
    error: documentDataError,
  } = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: { dataset_type: DOCUMENT_WORKSPACE_DATASET_TYPE, ...payload },
    callback: (data) => {
      if (currentTableState.pagination.total !== data.count) {
        tableStateFunctions.setPagination({
          ...currentTableState.pagination,
          total: data.count,
        });
      }
    },
  });
  const documentData = documentDataRaw as StudyDocumentData | undefined;

  /**
   * P0-5 / P0-7 — what a blinded viewer can see. Restricted documents are filtered out
   * entirely. This is a UI-level convenience filter, not enforcement — role_access is
   * checked server-side on retrieval, not by this list.
   */
  const visibleDocuments = useMemo(
    () =>
      (documentData?.results ?? []).filter((document) => {
        if (assumedRole && !document.role_access.includes(assumedRole))
          return false;
        return true;
      }),
    [assumedRole, documentData?.results],
  );

  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<StudyDocumentRow>();
  const selectedDocument = selectedRecord
    ? (documentData?.results.find((row) => row.id === selectedRecord.id) ??
      selectedRecord)
    : null;

  const columns = useMemo(
    () =>
      createDocumentColumns(
        currentTableState.columns
          .filter(
            (column: ColumnsType) =>
              column.visibility === ColumnVisibility.VISIBLE,
          )
          .map((column: ColumnsType) => column.fieldName),
        currentTableState.lockedColumns,
      ),
    [currentTableState.columns, currentTableState.lockedColumns],
  );

  const header = (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <PageHeader
          title={`Study Documents (${metadata.row_count})`}
          tooltipText="Study-scoped documents uploaded by the team, available to Ask Vivo in this study's context. Vivo holds working copies for AI context and team reference — it is not an eTMF or system of record."
        />
      </div>

      {canEdit ? (
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => setUploadOpen(true)}
        >
          Upload document
        </Button>
      ) : null}
    </div>
  );

  return (
    <div ref={pageRef} className="flex w-full min-w-0 flex-col gap-4 p-4">
      {header}

      <TableToolbar
        currentTableState={currentTableState}
        tableStateFunctions={tableStateFunctions}
        tableMetadata={metadata}
        maxMenuHeight={maxMenuHeight}
        fullPageName={DOCUMENT_WORKSPACE_DATASET_TYPE}
        endpointSuffix={BASE_ENDPOINT}
        name={DOCUMENT_WORKSPACE_DATASET_TYPE}
        datasetName={DOCUMENT_WORKSPACE_DATASET_TYPE}
      />
      <AppliedFiltersSection
        filters={currentTableState.filters}
        clearFilters={() => tableStateFunctions.setFilters({})}
        setFilters={tableStateFunctions.setFilters}
        filterOptions={metadata.filters}
        allFiltersData={metadata.filters}
      />
      {documentDataError ? (
        <ErrorComponent />
      ) : (
        <PaginatedTable
          data={visibleDocuments}
          columns={columns}
          loading={loadingDocumentData}
          setPagination={tableStateFunctions.setPagination}
          pagination={currentTableState.pagination}
          rowKey="id"
          onRow={getClickableRowProps}
        />
      )}

      <VersionHistoryDrawer
        document={selectedDocument}
        canEdit={canEdit}
        canManageAccess={canManageAccess}
        onClose={() => setSelectedRecord(null)}
        onUploadVersion={(document) => setVersioning(document)}
        onEditAccess={(document) => setEditingAccess(document)}
      />

      <RoleAccessModal
        document={editingAccess}
        onCancel={() => setEditingAccess(null)}
        onSave={async (roles) => {
          if (!editingAccess) return;
          try {
            await setRoleAccess({
              documentId: editingAccess.id,
              payload: { role_access: roles },
            });
            message.success(
              `Role access updated for “${editingAccess.title}”.`,
            );
          } catch {
            message.error("Couldn't update role access. Try again.");
          } finally {
            setEditingAccess(null);
          }
        }}
      />

      <NewVersionModal
        document={versioning}
        onCancel={() => setVersioning(null)}
        onSubmit={async (draft) => {
          if (!versioning) return;
          try {
            await addVersion({
              documentId: versioning.id,
              payload: buildNewVersionPayload(draft),
            });
            message.success(
              `v${versioning.current_version + 1} uploaded — v${versioning.current_version} superseded.`,
            );
            setVersioning(null);
          } catch {
            message.error("Couldn't upload the new version. Try again.");
          }
        }}
      />

      <UploadDocumentModal
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onUpload={async (values) => {
          try {
            await uploadDocument(buildUploadPayload(values));
            message.success(`“${values.title}” uploaded.`);
            setUploadOpen(false);
          } catch {
            message.error("Couldn't upload the document. Try again.");
          }
        }}
      />
    </div>
  );
}
